import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery, publicBaseQuery } from './baseQuery'
import type { I_AdminRow, I_CreateAdmin, I_CreatePlan, I_InviteDetails, I_UpdatePlan } from '../../logic/interfaces'
import {
  normalizeAssistantConfig,
  toCompanySummary,
  type AssistantConfig,
  type AssistantConfigSave,
  type CompanySummary,
} from '../../logic/interfaces/assistantConfig'
import {
  normalizeAssistantStats,
  normalizeUnansweredGroups,
  normalizeUnansweredPage,
  type AssistantRangeQuery,
  type AssistantStats,
  type AssistantStatsQuery,
  type UnansweredGroups,
  type UnansweredPage,
  type UnansweredQuery,
} from '../../logic/interfaces/assistantViews'
import { normalizeTradesmanSwitch, type TradesmanSwitch } from '../../logic/interfaces/tradesmanSwitch'
import type {
  MetricsExclusion,
  SubscriptionMetrics,
  SubscriptionMetricsQuery,
} from '../../logic/interfaces/subscriptionMetrics'

const ERROR_NOTIFICATION = {
  showSuccessNotifications: false,
  showErrorNotifications: true,
}

const FULL_NOTIFICATION = {
  showSuccessNotifications: true,
  showErrorNotifications: true,
}

const SILENT = {
  showSuccessNotifications: false,
  showErrorNotifications: false,
}

/** Global ticket counts — computed server-side, independent of the status filter. */
export interface TicketStatusCounts {
  total: number
  open: number
  replied: number
  closed: number
}

// ── AI Usage / Rates types ───────────────────────────────────
export interface AiUsageMetrics {
  inputTokens: number
  outputTokens: number
  transcriptionSeconds: number
  llmCostCents: number
  transcriptionCostCents: number
  requests: number
  totalCostCents: number
}

export interface AiModuleUsage extends AiUsageMetrics {
  module: string
  label: string
}

export interface AiMonthlyUsage {
  month: string
  byModule: Record<string, AiUsageMetrics>
  total: AiUsageMetrics
}

export interface CompanyAiUsage {
  total: AiUsageMetrics
  byModule: AiModuleUsage[]
  monthly: AiMonthlyUsage[]
}

export interface FleetAiUsageRow {
  companyId: string
  companyName: string | null
  total: AiUsageMetrics
}

export interface AiClaudeRate {
  inputPerMTokCents: number
  outputPerMTokCents: number
}

export interface AiRates {
  claudeRates: {
    haiku: AiClaudeRate
    sonnet: AiClaudeRate
    aiAgent: AiClaudeRate
  }
  transcriptionPerMinuteCents: number
  updatedBy: string | null
  updatedAt: string | null
}

export type UpdateAiRatesPayload = {
  claudeRates: {
    haiku: AiClaudeRate
    sonnet: AiClaudeRate
    aiAgent: AiClaudeRate
  }
  transcriptionPerMinuteCents: number
}

// ── Admin account / invite types ─────────────────────────────

/**
 * POST /admin/accept-invite answers `{ message }` and nothing else — it does
 * NOT issue a session. That is deliberate: minting an access token from an
 * unauthenticated endpoint is a credential-path risk not worth taking for a
 * one-time flow, so the invitee is handed to /login to sign in normally.
 */
export interface AdminAcceptInviteResponse {
  message?: string
}

/**
 * Body shared by POST /admin/accept-invite and POST /admin/reset-password.
 *
 * The key names are the BACKEND's (`AcceptAdminInviteDto` / `ResetAdminPasswordDto`,
 * which also match the authenticated `UpdateAdminPasswordDto`). The global
 * ValidationPipe runs `whitelist: true`, so any other spelling — `password` /
 * `confirmPassword` — is stripped off the body before the DTO is validated and
 * every submit 400s on "newPassword can not be empty". No aliases on either
 * side: three password paths must keep one vocabulary or they drift.
 */
/**
 * POST /admin/add-admin. The row is created FIRST and the invite mailed after,
 * so a mail-provider failure still answers 200 — with `inviteSent: false` and a
 * `message` that says so. Both must reach the dialog: an admin who is told
 * "Invite sent" never resends, and the invitee never gets in.
 */
export interface AdminCreateResponse {
  message?: string
  /** false = the account exists but no email went out; the row needs a Resend. */
  inviteSent?: boolean
  admin?: {
    _id: string
    email: string
    name?: string
    role: string
    status?: string
  }
}

/**
 * POST /admin/resend-invite/:adminId. Same non-fatal shape as `add-admin`: the
 * token is regenerated BEFORE the mail is handed to the provider, so a send
 * failure still answers 2xx — with `inviteSent: false` and a message saying the
 * link was reissued but not delivered. The old link is dead either way, so this
 * flag has to reach the operator: reported as success, nobody resends and the
 * invitee is left with no working link at all.
 */
export interface AdminInviteResendResponse {
  message?: string
  /** false = a fresh link exists but no email went out; it needs another Resend. */
  inviteSent?: boolean
}

export interface AdminTokenPasswordPayload {
  token: string
  newPassword: string
  confirmNewPassword: string
}

// ── E-signature (signing provider switch) types ─────────────
//
// `GET /admin/signing-provider?companyId=` — per signable kind the stored and
// effective provider with its last audited change, the four readiness checks
// (computed server-side on every call), the BoldSign documents still out, and
// the recorded A2P 10DLC confirmation behind the `a2p10dlc` check.

export type SigningProviderValue = 'boldsign' | 'inhouse'

export type SigningReadinessStatus = 'pass' | 'fail' | 'notChecked'

/** The server's four checks, in its order (`signing-readiness.ts`). */
export type SigningReadinessCheckKey = 'providerDefault' | 'objectLockBucket' | 'a2p10dlc' | 'signingLinkKey'

export interface SigningReadinessCheck {
  key: SigningReadinessCheckKey
  label: string
  status: SigningReadinessStatus
  detail: string
  /** ISO instant the fact dates from (A2P confirmed at), when known. */
  since?: string
}

export interface SigningReadiness {
  ready: boolean
  passCount: number
  total: number
  checks: SigningReadinessCheck[]
}

export interface SigningProviderChange {
  at: string | null
  byId: string
  byName: string | null
  reason: string | null
  from: SigningProviderValue | null
  to: SigningProviderValue | null
}

export interface SigningProviderKindRow {
  kind: string
  implemented: boolean
  stored: SigningProviderValue | null
  effective: SigningProviderValue
  lastChange: SigningProviderChange | null
}

/** A platform admin's recorded confirmation that the company's sending number is A2P 10DLC registered. */
export interface A2p10dlcConfirmation {
  confirmedAt: string
  confirmedById: string
  confirmedByName: string | null
  note: string
}

/** The newest A2P audit row: a confirmation (`reason` is its note) or a withdrawal (`reason` is why). */
export interface A2p10dlcConfirmationChange {
  action: 'confirmed' | 'withdrawn'
  at: string | null
  byId: string
  byName: string | null
  reason: string | null
}

export interface A2p10dlcConfirmationOverview {
  /** `null` when nothing is confirmed (never, or withdrawn). */
  confirmation: A2p10dlcConfirmation | null
  /** True exactly when there is a confirmation to withdraw. */
  canWithdraw: boolean
  lastChange: A2p10dlcConfirmationChange | null
}

export interface SigningProviderOverview {
  companyId: string
  kinds: SigningProviderKindRow[]
  readiness: SigningReadiness
  boldsignOutForSignature: Partial<Record<string, number>>
  a2p10dlcConfirmation: A2p10dlcConfirmationOverview
}

export interface SetSigningProviderPayload {
  companyId: string
  kind: string
  provider: SigningProviderValue
  reason: string
}

/** `POST /admin/signing-provider/a2p10dlc-confirmation` - the note is trimmed, 10..500 characters. */
export interface ConfirmA2p10dlcPayload {
  companyId: string
  note: string
}

/** `DELETE /admin/signing-provider/a2p10dlc-confirmation` - the reason is required, at most 500 characters. */
export interface WithdrawA2p10dlcPayload {
  companyId: string
  reason: string
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: axiosBaseQuery,
  tagTypes: [
    'Companies',
    'Admins',
    'Plans',
    'Tickets',
    'Settings',
    'Invoices',
    'ExtraUser',
    'AiRates',
    'SupportNotificationEmails',
    'AssistantConfig',
    'SigningProvider',
    'TradesmanSwitch',
    'SubscriptionMetrics',
  ],
  endpoints: (builder) => ({
    // ── Companies ──────────────────────────────────────────────

    getCompanies: builder.query<
      { companies: any[]; totalCount: number },
      { deleted: boolean; skip?: number; limit?: number; search?: string; status?: string; planType?: string }
    >({
      query: ({ deleted, ...params }) => {
        const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
        return {
          url: `/admin/all-companies/deleted/${deleted}`,
          params: filteredParams,
          interceptorConfig: ERROR_NOTIFICATION,
        }
      },
      transformResponse: (res: any) => ({
        companies: res?.companies ?? res ?? [],
        totalCount: res?.totalCount ?? res?.total ?? res?.count ?? (res?.companies ?? res ?? []).length,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.companies.map(({ _id }: any) => ({ type: 'Companies' as const, id: _id })),
              { type: 'Companies', id: 'LIST' },
            ]
          : [{ type: 'Companies', id: 'LIST' }],
    }),

    getCompanyById: builder.query<any, string>({
      query: (companyId) => ({
        url: `/admin/company-by-id/companyId/${companyId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      providesTags: (_result, _error, companyId) => [{ type: 'Companies', id: companyId }],
      transformResponse: (res: any) => ({
        company: res?.company,
        companySetting: res?.companySetting,
      }),
    }),

    // ── Subscription metrics (plan subscription-metrics) ───────

    getSubscriptionMetrics: builder.query<SubscriptionMetrics, SubscriptionMetricsQuery>({
      query: ({ period, planType }) => ({
        url: `/admin/subscription-metrics`,
        params: { period, ...(planType && { planType }) },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      providesTags: ['SubscriptionMetrics'],
    }),

    // Flipping the exclusion moves every metric and this company's detail.
    setMetricsExclusion: builder.mutation<MetricsExclusion, MetricsExclusion>({
      query: ({ companyId, excludeFromMetrics }) => ({
        url: `/admin/company/${companyId}/metrics-exclusion`,
        method: 'PATCH',
        data: { excludeFromMetrics },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: (_result, error, { companyId }) =>
        error ? [] : ['SubscriptionMetrics', { type: 'Companies', id: companyId }],
    }),

    getCompanyAnalytics: builder.query<any, string>({
      query: (companyId) => ({
        url: `/admin/company-analytics/companyId/${companyId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
    }),

    deleteCompany: builder.mutation<void, string>({
      query: (companyId) => ({
        url: `/admin/company/${companyId}`,
        method: 'DELETE',
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Companies', id: 'LIST' }],
    }),

    restoreCompany: builder.mutation<void, string>({
      query: (companyId) => ({
        url: `/admin/restore-company/${companyId}`,
        method: 'PATCH',
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Companies', id: 'LIST' }],
    }),

    permDeleteCompany: builder.mutation<void, string[]>({
      query: (companyIds) => ({
        url: `/admin/company-permanently`,
        method: 'DELETE',
        data: { companyIds },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Companies', id: 'LIST' }],
    }),

    // ── Admins ─────────────────────────────────────────────────

    // `total` comes back alongside `admins` and must be kept: it is what the
    // pagination footer reads. Discarding it here (the old `res?.admins ?? []`)
    // left the table's redux `totalCount` on its default 0, so the footer said
    // "0-0 of 0" over a full page of rows.
    getAdmins: builder.query<{ admins: I_AdminRow[]; total: number }, void>({
      query: () => ({
        url: `/admin/admin-list`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: unknown) => {
        const body = res as { admins?: I_AdminRow[]; total?: number } | undefined
        const admins = body?.admins ?? []
        return { admins, total: typeof body?.total === 'number' ? body.total : admins.length }
      },
      providesTags: (result) =>
        result
          ? [...result.admins.map(({ _id }) => ({ type: 'Admins' as const, id: _id })), { type: 'Admins', id: 'LIST' }]
          : [{ type: 'Admins', id: 'LIST' }],
    }),

    // SILENT on purpose: the Add Admin dialog owns every outcome itself — a
    // duplicate email (409) opens the "already has an account" alert rather
    // than a toast, and the caller re-raises anything else as a toast.
    createAdmin: builder.mutation<AdminCreateResponse, I_CreateAdmin>({
      query: (data) => ({
        url: `/admin/add-admin`,
        method: 'POST',
        data,
        interceptorConfig: SILENT,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),

    // Re-issues the invite email (and a fresh 72h token) for a row that is
    // still Invited. Offered only on those rows. Typed with `inviteSent` so the
    // caller can tell "mailed" from "regenerated but not mailed" — the previous
    // `{ message?: string }` hid the flag from the type system and every
    // outcome rendered as a green success toast.
    resendAdminInvite: builder.mutation<AdminInviteResendResponse, string>({
      query: (adminId) => ({
        url: `/admin/resend-invite/${adminId}`,
        method: 'POST',
        data: {},
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),

    // SILENT, like `createAdmin`: the confirm dialog owns every outcome. The
    // refusals these routes return are instructions the operator has to act on
    // — the last-Super-Admin rule, and the 409 raised when the target still
    // holds the platform API key ("generate a new key from Settings first") —
    // and a toast fired from the interceptor lands BEHIND the still-open modal.
    // The caller renders the message in the dialog and re-raises nothing.
    deleteAdmin: builder.mutation<{ message?: string }, string>({
      query: (adminId) => ({
        url: `/admin/delete-admin/${adminId}`,
        method: 'DELETE',
        interceptorConfig: SILENT,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),

    permDeleteAdmin: builder.mutation<{ message?: string }, string>({
      query: (adminId) => ({
        url: `/admin/perm-delete/${adminId}`,
        method: 'DELETE',
        interceptorConfig: SILENT,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),

    restoreAdmin: builder.mutation<{ message?: string }, string>({
      query: (adminId) => ({
        url: `/admin/restore-access/${adminId}`,
        method: 'PATCH',
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),

    // ── Plans ──────────────────────────────────────────────────

    getPlans: builder.query<any[], void>({
      query: () => ({
        url: `/admin/fetch-plans`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => res?.plans ?? [],
      providesTags: (result) =>
        result
          ? [...result.map(({ _id }: any) => ({ type: 'Plans' as const, id: _id })), { type: 'Plans', id: 'LIST' }]
          : [{ type: 'Plans', id: 'LIST' }],
    }),

    getPlanById: builder.query<any, string>({
      query: (planId) => ({
        url: `/admin/fetch-plan/${planId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => res?.plan ?? null,
      providesTags: (_result, _error, planId) => [{ type: 'Plans', id: planId }],
    }),

    createPlan: builder.mutation<any, I_CreatePlan>({
      query: (data) => ({
        url: `/admin/create-plan`,
        method: 'POST',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: [{ type: 'Plans', id: 'LIST' }],
    }),

    updatePlan: builder.mutation<any, { data: I_UpdatePlan; planId: string }>({
      query: ({ data, planId }) => ({
        url: `/admin/updatePlan/${planId}`,
        method: 'PUT',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: (_result, _error, { planId }) => [
        { type: 'Plans', id: planId },
        { type: 'Plans', id: 'LIST' },
      ],
    }),

    // ── Support Tickets ────────────────────────────────────────

    getTickets: builder.query<
      { tickets: any[]; total: number; statusCounts: TicketStatusCounts },
      { companyId?: string; status?: string | number; search?: string; skip?: number; limit?: number } | undefined
    >({
      query: (params) => ({
        url: `/admin/support/all-tickets`,
        params: {
          ...(params?.companyId && { companyId: params.companyId }),
          ...(params?.status && { status: params.status }),
          ...(params?.search && { search: params.search }),
          skip: params?.skip ?? 0,
          limit: params?.limit ?? 25,
        },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => ({
        tickets: res?.tickets ?? [],
        total: res?.total ?? res?.tickets?.length ?? 0,
        // `statusCounts` is computed server-side over the unfiltered-by-status
        // query, so the summary chips stay global instead of describing the
        // current filter.
        statusCounts: res?.statusCounts ?? { total: 0, open: 0, replied: 0, closed: 0 },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.tickets.map(({ _id }: any) => ({ type: 'Tickets' as const, id: _id })),
              { type: 'Tickets', id: 'LIST' },
            ]
          : [{ type: 'Tickets', id: 'LIST' }],
    }),

    getTicketById: builder.query<any, { companyId: string; ticketId: string }>({
      query: ({ companyId, ticketId }) => ({
        url: `/admin/support/ticket-by-id/${companyId}/${ticketId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => res?.ticket ?? null,
      providesTags: (_result, _error, { ticketId }) => [{ type: 'Tickets', id: ticketId }],
    }),

    replyToTicket: builder.mutation<any, any>({
      query: (data) => ({
        url: `/admin/support/reply-query`,
        method: 'PATCH',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Tickets', id: arg?.ticketId },
        { type: 'Tickets', id: 'LIST' },
      ],
    }),

    // Clears the unread flag on the customer's replies once an admin opens the
    // thread. The admin console never called this, so `isSeen` stayed false
    // forever on the user side.
    markTicketRepliesSeen: builder.mutation<any, { ticketId: string }>({
      query: ({ ticketId }) => ({
        url: `/admin/support/mark-seen/ticket/${ticketId}`,
        method: 'PATCH',
        data: {},
        interceptorConfig: SILENT,
      }),
      invalidatesTags: (_result, _error, { ticketId }) => [{ type: 'Tickets', id: ticketId }],
    }),

    closeTicket: builder.mutation<void, { companyId: string; ticketId: string }>({
      query: ({ companyId, ticketId }) => ({
        url: `/admin/support/close-ticket?companyId=${companyId}&ticketId=${ticketId}&memberId=support`,
        method: 'PATCH',
        data: {},
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: (_result, _error, { ticketId }) => [
        { type: 'Tickets', id: ticketId },
        { type: 'Tickets', id: 'LIST' },
      ],
    }),

    // ── Settings ───────────────────────────────────────────────

    getAdminProfile: builder.query<any, string>({
      query: (adminId) => ({
        url: `/admin/by-id/${adminId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => res?.profile ?? {},
      providesTags: ['Settings'],
    }),

    generateApiKey: builder.mutation<any, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        let secret = ''
        for (let i = 0; i < 10; i++) secret += characters.charAt(Math.floor(Math.random() * characters.length))
        return baseQuery({
          url: `/admin/generate-api-key/${secret}`,
          method: 'PATCH',
          interceptorConfig: ERROR_NOTIFICATION,
        })
      },
      invalidatesTags: ['Settings'],
    }),

    getExtraUser: builder.query<
      { extraUserChargeMonthly: number; extraUserChargeYearly: number; baseUserCount: number },
      void
    >({
      query: () => ({
        url: `/admin/extra-user-amount`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => ({
        extraUserChargeMonthly: res?.extraAmount?.extraUserChargeMonthly ?? 0,
        extraUserChargeYearly: res?.extraAmount?.extraUserChargeYearly ?? 0,
        baseUserCount: res?.extraAmount?.baseUserCount ?? 0,
      }),
      providesTags: ['ExtraUser'],
    }),

    updateExtraUser: builder.mutation<
      any,
      { extraUserChargeMonthly?: number; extraUserChargeYearly?: number; baseUserCount?: number }
    >({
      query: (data) => ({
        url: `/admin/extra-user-amount`,
        method: 'PUT',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: ['ExtraUser'],
    }),

    updateNotificationEmail: builder.mutation<
      any,
      { notificationEmail: string; subjectLine?: string; messageBody?: string }
    >({
      query: (data) => ({
        url: `/admin/notification-email`,
        method: 'PUT',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      invalidatesTags: ['Settings'],
    }),

    // ── Support ticket notification recipients (NHR-2866) ──────
    // Platform-level list; read and written against the SuperAdmin record so
    // every admin sees and edits the same setting.
    getSupportNotificationEmails: builder.query<string[], void>({
      query: () => ({
        url: `/admin/support-notification-emails`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => res?.supportNotificationEmails ?? [],
      providesTags: ['SupportNotificationEmails'],
    }),

    updateSupportNotificationEmails: builder.mutation<any, { supportNotificationEmails: string[] }>({
      query: (data) => ({
        url: `/admin/support-notification-emails`,
        method: 'PUT',
        data,
        interceptorConfig: FULL_NOTIFICATION,
      }),
      invalidatesTags: ['SupportNotificationEmails'],
    }),

    updateAdminPassword: builder.mutation<
      any,
      { oldPassword: string; newPassword: string; confirmNewPassword: string }
    >({
      query: (data) => ({
        url: `/admin/update-password`,
        method: 'PATCH',
        data,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
    }),

    // ── Invoices ───────────────────────────────────────────────

    getCompanyInvoices: builder.query<any, string>({
      query: (companyId) => ({
        url: `/admin/company-invoices/${companyId}`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      providesTags: (_result, _error, companyId) => [{ type: 'Invoices', id: companyId }],
    }),

    // ── AI Usage ───────────────────────────────────────────────

    getCompanyAiUsage: builder.query<CompanyAiUsage, { companyId: string; startMonth?: string; endMonth?: string }>({
      query: ({ companyId, startMonth, endMonth }) => ({
        url: `/admin/company-analytics/ai-usage/companyId/${companyId}`,
        params: {
          ...(startMonth && { startMonth }),
          ...(endMonth && { endMonth }),
        },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
    }),

    getFleetAiUsage: builder.query<FleetAiUsageRow[], { month?: string }>({
      query: ({ month } = {}) => ({
        url: `/admin/company-analytics/ai-usage`,
        params: {
          ...(month && { month }),
        },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
    }),

    // ── AI Cost Rates ──────────────────────────────────────────

    getAiRates: builder.query<AiRates, void>({
      query: () => ({
        url: `/admin/ai-rates`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      providesTags: ['AiRates'],
    }),

    updateAiRates: builder.mutation<AiRates, UpdateAiRatesPayload>({
      query: (data) => ({
        url: `/admin/ai-rates`,
        method: 'PUT',
        data,
        interceptorConfig: FULL_NOTIFICATION,
      }),
      invalidatesTags: ['AiRates'],
    }),

    // ── PWP Assistant config (SuperAdmin only) ─────────────────

    // A 403 here is rendered in-page (stale stored role), and the interceptor
    // never toasts a 403 anyway; every other failure toasts as usual.
    getAssistantConfig: builder.query<AssistantConfig, void>({
      query: () => ({
        url: `/admin/assistant/config`,
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: unknown) => normalizeAssistantConfig(res),
      providesTags: ['AssistantConfig'],
    }),

    // PUT is a full replace. Nothing is invalidated on a failed save, so the
    // page keeps showing the config the server is still running with.
    // SILENT: a 409 is answered with a banner, not a toast, so the form toasts
    // the other failures itself (`saveFailureToast`).
    updateAssistantConfig: builder.mutation<AssistantConfig, AssistantConfigSave>({
      query: (data) => ({
        url: `/admin/assistant/config`,
        method: 'PUT',
        data,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeAssistantConfig(res),
      invalidatesTags: (_result, error) => (error ? [] : ['AssistantConfig']),
    }),

    // Force-off: no body and no revision, so it works without a loaded config and over a conflicting save.
    // Only `enabled` changes on the server; the response is the whole config, same shape as GET.
    disableAssistant: builder.mutation<AssistantConfig, void>({
      query: () => ({
        url: `/admin/assistant/config/disable`,
        method: 'POST',
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      transformResponse: (res: unknown) => normalizeAssistantConfig(res),
      invalidatesTags: (_result, error) => (error ? [] : ['AssistantConfig']),
    }),

    // Same route as `getCompanyById`, trimmed to what a company chip needs and
    // SILENT: an allowlisted id whose company was deleted is an expected state
    // ("Unknown company" chip), not an error toast per chip.
    getCompanySummary: builder.query<CompanySummary | null, string>({
      query: (companyId) => ({
        url: `/admin/company-by-id/companyId/${companyId}`,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => toCompanySummary((res as { company?: unknown } | undefined)?.company),
      providesTags: (_result, _error, companyId) => [{ type: 'Companies', id: companyId }],
    }),

    // -- PWP Assistant unanswered + stats (SuperAdmin only, read-only) --

    // SILENT: each tab draws its own state for every failure (failed card,
    // inline 400 under the dates, in-page 403), so a toast would say it twice.
    // Nothing is kept once a tab lets go of it: the numbers are always asked
    // for again, never read back from an earlier visit.
    getAssistantUnanswered: builder.query<UnansweredPage, UnansweredQuery>({
      query: (params) => ({
        url: `/admin/assistant/unanswered`,
        params,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeUnansweredPage(res),
      keepUnusedDataFor: 0,
    }),

    getAssistantUnansweredGroups: builder.query<UnansweredGroups, AssistantRangeQuery>({
      query: (params) => ({
        url: `/admin/assistant/unanswered/groups`,
        params,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeUnansweredGroups(res),
      keepUnusedDataFor: 0,
    }),

    getAssistantStats: builder.query<AssistantStats, AssistantStatsQuery>({
      query: (params) => ({
        url: `/admin/assistant/stats`,
        params,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeAssistantStats(res),
      keepUnusedDataFor: 0,
    }),

    // ── E-signature (signing provider switch, SuperAdmin only) ─

    getSigningProvider: builder.query<SigningProviderOverview, string>({
      query: (companyId) => ({
        url: `/admin/signing-provider`,
        params: { companyId },
        interceptorConfig: ERROR_NOTIFICATION,
      }),
      providesTags: (_result, _error, companyId) => [{ type: 'SigningProvider', id: companyId }],
    }),

    // SILENT: a refusal (reason missing, readiness not met, changed concurrently)
    // is rendered inside the still-open confirmation dialog, not as a toast.
    setSigningProvider: builder.mutation<{ message?: string }, SetSigningProviderPayload>({
      query: (data) => ({
        url: `/admin/signing-provider`,
        method: 'PATCH',
        data,
        interceptorConfig: SILENT,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [{ type: 'SigningProvider', id: companyId }],
    }),

    // The A2P 10DLC readiness check passes on a recorded confirmation. SILENT for the same reason
    // as the switch: a refusal (bad note, already confirmed, nothing to withdraw, changed
    // concurrently) is shown in the open dialog. Invalidated on a 409 too, so the tab catches up
    // with whatever another operator recorded.
    confirmA2p10dlc: builder.mutation<{ message?: string }, ConfirmA2p10dlcPayload>({
      query: (data) => ({
        url: `/admin/signing-provider/a2p10dlc-confirmation`,
        method: 'POST',
        data,
        interceptorConfig: SILENT,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [{ type: 'SigningProvider', id: companyId }],
    }),

    withdrawA2p10dlc: builder.mutation<{ message?: string }, WithdrawA2p10dlcPayload>({
      query: (data) => ({
        url: `/admin/signing-provider/a2p10dlc-confirmation`,
        method: 'DELETE',
        data,
        interceptorConfig: SILENT,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [{ type: 'SigningProvider', id: companyId }],
    }),

    // ── Tradesman crew hide switch (SuperAdmin only, nhrapp#4397) ─

    // SILENT: the card draws a failed load itself; a refused write shows in the confirm dialog (turning on)
    // or in the card's own toast (turning off).
    getTradesmanSwitch: builder.query<TradesmanSwitch | null, string>({
      query: (companyId) => ({
        url: `/admin/tradesman-switch`,
        params: { companyId },
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeTradesmanSwitch(res),
      providesTags: (_result, _error, companyId) => [{ type: 'TradesmanSwitch', id: companyId }],
    }),

    setTradesmanSwitch: builder.mutation<TradesmanSwitch | null, TradesmanSwitch>({
      query: (data) => ({
        url: `/admin/tradesman-switch`,
        method: 'PATCH',
        data,
        interceptorConfig: SILENT,
      }),
      transformResponse: (res: unknown) => normalizeTradesmanSwitch(res),
      invalidatesTags: (_result, _error, { companyId }) => [{ type: 'TradesmanSwitch', id: companyId }],
    }),

    // ── Public / unauthenticated (AdminOpenController) ─────────
    //
    // These four run through `publicBaseQuery`, NOT the shared `axiosBaseQuery`.
    // The visitor is logged out by definition, and an expired invite / reset
    // token answers 401 — on the authenticated instance that 401 would be fed
    // to the refresh-then-performLogout() path, wiping localStorage and hard-
    // redirecting to '/'. `publicBaseQuery` has no interceptors: no bearer
    // attached, no refresh, no bounce, no toast (the pages render their own
    // states). See src/store/api/baseQuery.ts.

    getAdminInvite: builder.query<I_InviteDetails, string>({
      queryFn: (token) => publicBaseQuery<I_InviteDetails>({ url: `/admin/invite/${token}` }),
    }),

    acceptAdminInvite: builder.mutation<AdminAcceptInviteResponse, AdminTokenPasswordPayload>({
      queryFn: (data) =>
        publicBaseQuery<AdminAcceptInviteResponse>({ url: `/admin/accept-invite`, method: 'POST', data }),
    }),

    // Always resolves 200 with the same message whether or not the address
    // exists — the response must not be usable to enumerate admin accounts.
    forgotAdminPassword: builder.mutation<{ message?: string }, { email: string }>({
      queryFn: (data) => publicBaseQuery<{ message?: string }>({ url: `/admin/forgot-password`, method: 'POST', data }),
    }),

    resetAdminPassword: builder.mutation<{ message?: string }, AdminTokenPasswordPayload>({
      queryFn: (data) => publicBaseQuery<{ message?: string }>({ url: `/admin/reset-password`, method: 'POST', data }),
    }),
  }),
})

export const {
  // Companies
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetCompanyAnalyticsQuery,
  useDeleteCompanyMutation,
  useRestoreCompanyMutation,
  usePermDeleteCompanyMutation,
  // Subscription metrics
  useGetSubscriptionMetricsQuery,
  useSetMetricsExclusionMutation,
  // Admins
  useGetAdminsQuery,
  useCreateAdminMutation,
  useResendAdminInviteMutation,
  useDeleteAdminMutation,
  usePermDeleteAdminMutation,
  useRestoreAdminMutation,
  // Plans
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  // Tickets
  useGetTicketsQuery,
  useGetTicketByIdQuery,
  useReplyToTicketMutation,
  useCloseTicketMutation,
  useMarkTicketRepliesSeenMutation,
  // Settings
  useGetSupportNotificationEmailsQuery,
  useUpdateSupportNotificationEmailsMutation,
  useGetAdminProfileQuery,
  useGenerateApiKeyMutation,
  useGetExtraUserQuery,
  useUpdateExtraUserMutation,
  useUpdateNotificationEmailMutation,
  useUpdateAdminPasswordMutation,
  // Invoices
  useGetCompanyInvoicesQuery,
  // AI Usage
  useGetCompanyAiUsageQuery,
  useGetFleetAiUsageQuery,
  // AI Cost Rates
  useGetAiRatesQuery,
  useUpdateAiRatesMutation,
  // PWP Assistant config
  useGetAssistantConfigQuery,
  useUpdateAssistantConfigMutation,
  useDisableAssistantMutation,
  useLazyGetCompanySummaryQuery,
  // PWP Assistant unanswered + stats
  useLazyGetAssistantUnansweredQuery,
  useGetAssistantUnansweredGroupsQuery,
  useGetAssistantStatsQuery,
  // E-signature
  useGetSigningProviderQuery,
  useSetSigningProviderMutation,
  useConfirmA2p10dlcMutation,
  useWithdrawA2p10dlcMutation,
  // Tradesman crew hide switch
  useGetTradesmanSwitchQuery,
  useSetTradesmanSwitchMutation,
  // Public (unauthenticated)
  useGetAdminInviteQuery,
  useAcceptAdminInviteMutation,
  useForgotAdminPasswordMutation,
  useResetAdminPasswordMutation,
} = adminApi
